/**
 * THE PIT'S ANSWER CLOCK — the 15s a duellist has to commit a move.
 *
 * The clock is a PRESENTATION device: the server resolves both halves of a
 * round in one call and stays authoritative, while the ring paces it as two
 * turns (see FightScreen's HOLMGANG PACING block). Because of that it lived
 * purely in component state — and a page refresh remounted the component, so
 * the player got a fresh 15s every reload and could stall a duel forever
 * (QA 86cb8z0xk: "15s turn timeout resets on page refresh. Should keep the
 * state as was.").
 *
 * The fix is to stop storing "seconds left" and store the DEADLINE instead — an
 * absolute timestamp parked in sessionStorage, the same trick the elapsed-fight
 * clock already uses (`fw:fight-clock-start`). A reload restores the turn as it
 * stood; a turn that ran out while the tab was away is already over when the
 * player comes back.
 *
 * Everything here is pure apart from the storage handle, which is injected so
 * the rules can be unit-tested without a DOM.
 */

/** Seconds a duellist gets to answer. */
export const TURN_SECONDS = 15;

/** sessionStorage key holding the current turn's deadline (epoch ms). */
export const TURN_DEADLINE_KEY = 'fw:fight-turn-deadline';

/** The slice of the Storage API this module needs. */
export type TurnStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** sessionStorage, or null where it is unavailable (private mode, disabled). */
export function turnStore(): TurnStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The stored deadline (epoch ms), or null when nothing usable is stored. */
export function readTurnDeadline(store: TurnStore | null = turnStore()): number | null {
  if (!store) return null;
  try {
    const raw = Number(store.getItem(TURN_DEADLINE_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  } catch {
    return null;
  }
}

/** Park a deadline for the turn now under way. */
export function writeTurnDeadline(at: number, store: TurnStore | null = turnStore()): void {
  if (!store) return;
  try {
    store.setItem(TURN_DEADLINE_KEY, String(at));
  } catch {
    /* storage full or blocked — the clock just won't survive a reload */
  }
}

/** Drop the stored deadline: the turn was answered, or the bout is over. */
export function clearTurnDeadline(store: TurnStore | null = turnStore()): void {
  if (!store) return;
  try {
    store.removeItem(TURN_DEADLINE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * The deadline a turn starting now should run to: the one already stored (so a
 * reload picks the same turn back up) or a fresh `TURN_SECONDS` from now.
 * Returns the deadline plus whether it was restored, so the caller can tell a
 * resumed turn from a new one.
 */
export function resolveTurnDeadline(
  now = Date.now(),
  store: TurnStore | null = turnStore(),
): { deadline: number; restored: boolean } {
  const stored = readTurnDeadline(store);
  if (stored !== null) return { deadline: stored, restored: true };
  const deadline = now + TURN_SECONDS * 1000;
  writeTurnDeadline(deadline, store);
  return { deadline, restored: false };
}

/**
 * Whole seconds left on a deadline, for the countdown and its bar. Rounded UP
 * so the last fraction of a second still reads as "1", clamped into
 * 0..TURN_SECONDS so a stale far-future deadline can never draw a bar past
 * full. 0 means the turn has lapsed. PURE.
 */
export function turnSecondsLeft(deadline: number, now = Date.now()): number {
  const left = Math.ceil((deadline - now) / 1000);
  if (left <= 0) return 0;
  return Math.min(TURN_SECONDS, left);
}
